import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function TenantLogin() {
  const [mode, setMode] = useState("password");
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");
  const navigate = useNavigate();
  // Strip country code / trunk zero if user pastes the full number (e.g. 919999900001 → 9999900001)
  const normalizeLocal = (raw) => {
    let d = raw.replace(/\D/g, "");
    const cc = countryCode.replace(/\D/g, "");
    if (d.startsWith(cc) && d.length - cc.length === 10) d = d.slice(cc.length);
    if (d.startsWith("0") && d.length === 11) d = d.slice(1);
    return d;
  };
  const phone = countryCode + phoneLocal;

  useEffect(() => {
    api.get("/tenant-portal/me").then(() => navigate("/tenant/home")).catch(() => {});
  }, [navigate]);

  const loginPassword = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/tenant-portal/login-password", { phone, password });
      toast.success("Welcome"); navigate("/tenant/home");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const requestOtp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await api.post("/tenant-portal/request-otp", { phone });
      setStep("otp"); setHint("If your number is registered, a verification code has been sent to it.");
      toast.success("OTP sent");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/tenant-portal/verify-otp", { phone, otp });
      toast.success("Welcome"); navigate("/tenant/home");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex bg-slate-900 text-white p-12 flex-col justify-between">
        <Link to="/" className="font-display text-2xl font-black tracking-tighter">
          Stay<span className="text-blue-500">Care</span>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Tenant portal</div>
          <h2 className="font-display text-5xl font-black tracking-tighter leading-none">
            Pay rent. Raise issues. Stay informed.
          </h2>
          <p className="mt-4 text-slate-400 max-w-md">
            Sign in with your registered phone — password or one-time code.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">© StayCare</div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-slate-50">
        <div className="w-full max-w-md space-y-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Tenant sign-in</div>
            <h1 className="font-display text-3xl font-black tracking-tighter mt-1">Welcome back</h1>
          </div>

          <div className="flex gap-1 border border-slate-200 p-1 bg-white">
            <button onClick={() => { setMode("password"); setStep("phone"); }}
                    data-testid="mode-password"
                    className={`flex-1 py-2 text-sm font-medium ${
                      mode === "password" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                    }`}>Password</button>
            <button onClick={() => { setMode("otp"); setStep("phone"); }}
                    data-testid="mode-otp"
                    className={`flex-1 py-2 text-sm font-medium ${
                      mode === "otp" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                    }`}>One-time code</button>
          </div>

          {mode === "password" ? (
            <form onSubmit={loginPassword} className="space-y-4" data-testid="tenant-password-form">
              <div className="flex gap-2">
                <select data-testid="tenant-country-code" value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-24 border border-slate-300 px-2 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+971">🇦🇪 +971</option>
                  <option value="+65">🇸🇬 +65</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+966">🇸🇦 +966</option>
                  <option value="+254">🇰🇪 +254</option>
                  <option value="+255">🇹🇿 +255</option>
                  <option value="+976">🇲🇳 +976</option>
                </select>
                <input required type="tel" inputMode="numeric" placeholder="9876543210"
                       data-testid="tenant-phone-input" maxLength={14}
                       className="flex-1 border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                       value={phoneLocal} onChange={(e) => setPhoneLocal(normalizeLocal(e.target.value))} />
              </div>
              <div className="relative">
                <input required type={showPw ? "text" : "password"} placeholder="Password"
                       data-testid="tenant-password-input"
                       className="w-full border border-slate-300 px-4 py-3 pr-11 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                       value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="button" onClick={() => setShowPw(!showPw)}
                        data-testid="tenant-toggle-password"
                        aria-label={showPw ? "Hide password" : "Show password"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-slate-400 hover:text-slate-700">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button disabled={loading} data-testid="tenant-password-submit"
                      className="w-full bg-blue-600 text-white py-3.5 font-medium hover:bg-blue-700 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {loading ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          ) : step === "phone" ? (
            <form onSubmit={requestOtp} className="space-y-4" data-testid="tenant-phone-form">
              <div className="flex gap-2">
                <select data-testid="tenant-country-code-otp" value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-24 border border-slate-300 px-2 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+971">🇦🇪 +971</option>
                  <option value="+65">🇸🇬 +65</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+966">🇸🇦 +966</option>
                  <option value="+254">🇰🇪 +254</option>
                  <option value="+255">🇹🇿 +255</option>
                  <option value="+976">🇲🇳 +976</option>
                </select>
                <input required type="tel" inputMode="numeric" placeholder="9876543210"
                       data-testid="tenant-phone-input" maxLength={14}
                       className="flex-1 border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                       value={phoneLocal} onChange={(e) => setPhoneLocal(normalizeLocal(e.target.value))} />
              </div>
              <button disabled={loading} data-testid="tenant-request-otp"
                      className="w-full bg-blue-600 text-white py-3.5 font-medium hover:bg-blue-700 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {loading ? "Sending…" : <>Send code <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4" data-testid="tenant-otp-form">
              <input required type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                     placeholder="123456" data-testid="tenant-otp-input"
                     className="w-full border border-slate-300 px-4 py-3 bg-white text-center font-mono tracking-[0.5em] text-2xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                     value={otp} onChange={(e) => setOtp(e.target.value)} />
              {hint && <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 text-sm font-medium" data-testid="otp-hint">
                {hint}
              </div>}
              <button disabled={loading} data-testid="tenant-verify-otp"
                      className="w-full bg-blue-600 text-white py-3.5 font-medium hover:bg-blue-700 disabled:opacity-60">
                {loading ? "Verifying…" : "Verify & sign in"}
              </button>
              <button type="button" onClick={() => setStep("phone")}
                      className="w-full text-sm text-slate-500 hover:text-slate-700">
                ← Use a different number
              </button>
            </form>
          )}

          <div className="text-xs text-slate-500 border-t border-slate-200 pt-4">
            Are you a landlord? <Link className="text-blue-600 hover:underline" to="/login">Sign in here</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
